import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getIndustryBySlug } from '../data/industries/industryTrainingsData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Clock,
  Award,
  Users,
  TrendingUp
} from 'lucide-react';
import { UpcomingTrainingsSection } from '../components/UpcomingTrainingsSection';

export const IndustryDetail = () => {
  const { industrySlug } = useParams();
  const navigate = useNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const industry = getIndustryBySlug(industrySlug);

  if (!industry) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Industry Not Found</h1>
        <p className="text-gray-600 mb-8">The industry you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/industry-training')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Industries
        </Button>
      </div>
    );
  }

  const Icon = industry.icon;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  // SEO configuration
  const SITE_URL = 'https://www.isde.academy';
  const canonicalUrl = `${SITE_URL}/industry-training/${industrySlug}`;

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Industry Training",
        "item": `${SITE_URL}/industry-training`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": industry.name,
        "item": canonicalUrl
      }
    ]
  };

  // ItemList structured data for training programs
  const itemListStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${industry.name} Training Programs`,
    "description": industry.overview,
    "numberOfItems": industry.trainings.length,
    "itemListElement": industry.trainings.map((training, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Course",
        "name": training.title,
        "description": training.shortDescription,
        "url": `${SITE_URL}/industry-training/${industrySlug}/${training.slug}`,
        "provider": {
          "@type": "Organization",
          "name": "ISDE Academy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{industry.name} Training Programs | ISDE Academy</title>
        <meta name="description" content={industry.overview} />
        <meta
          name="keywords"
          content={`${industry.name.toLowerCase()} training india, ${industry.trainings.map(t => t.title.toLowerCase()).slice(0, 5).join(', ')}, professional certification`}
        />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={`${industry.name} Training Programs | ISDE Academy`} />
        <meta property="og:description" content={industry.overview} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />

        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>

        {/* ItemList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(itemListStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white pt-40 pb-12 overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${industry.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 to-indigo-900/90" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-blue-200 mb-6">
              <button onClick={() => navigate('/industry-training')} className="hover:text-white">
                Industry Training
              </button>
              <span>/</span>
              <span className="text-white">{industry.name}</span>
            </div>

            <div className="flex items-center gap-6 mb-6">
              <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Icon className="h-10 w-10 text-white" />
              </div>
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-2">{industry.title}</h1>
                <p className="text-xl text-blue-100">{industry.description}</p>
              </div>
            </div>

            <p className="text-lg text-blue-100 max-w-4xl leading-relaxed">
              {industry.overview}
            </p>
          </motion.div>
        </div>

        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Benefits & Target Audience */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Key Benefits</h2>
              </div>
              <div className="space-y-3">
                {industry.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start gap-3 p-4 rounded-lg bg-green-50 hover:bg-green-100 transition-colors"
                  >
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Target Audience */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Who Should Attend</h2>
              </div>
              <div className="space-y-3">
                {industry.targetAudience.map((audience, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center gap-3 p-4 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors"
                  >
                    <Users className="h-5 w-5 text-purple-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{audience}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Training Programs */}
      <section ref={ref} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Available <span className="text-blue-600">Training Programs</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {industry.trainings.length} specialized training programs designed specifically for the {industry.name.toLowerCase()} sector
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {industry.trainings.map((training) => {
              const TrainingIcon = training.icon;
              return (
                <motion.div
                  key={training.id}
                  variants={cardVariants}
                >
                  <Card
                    className="group h-full hover:shadow-2xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-blue-50/30 cursor-pointer overflow-hidden relative"
                    onClick={() => navigate(`/industry-training/${industry.slug}/${training.slug}`)}
                  >
                    {/* Decorative gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                    {/* Header with gradient */}
                    <div className="relative h-24 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
                      <TrainingIcon className="h-12 w-12 text-white" />
                      {training.certificationLevels && (
                        <Badge className="absolute top-3 right-3 bg-yellow-400 text-gray-900 border-0">
                          {training.certificationLevels.length} Levels
                        </Badge>
                      )}
                    </div>

                    <CardHeader className="pb-3 relative z-10">
                      <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight line-clamp-2 mb-3">
                        {training.title}
                      </CardTitle>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {training.shortDescription}
                      </p>
                    </CardHeader>

                    <CardContent className="space-y-4 relative z-10">
                      {/* Training details */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">Duration:</span>
                          <span>{training.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Award className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">Level:</span>
                          <span>{training.level}</span>
                        </div>
                        {training.deliveryMode && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
                            <span className="font-medium">Mode:</span>
                            {training.deliveryMode.map((mode, idx) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                              >
                                {mode}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* CTA */}
                      <div className="pt-4 border-t border-gray-100">
                        <Button
                          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/industry-training/${industry.slug}/${training.slug}`);
                          }}
                        >
                          View Details
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Upcoming Trainings for this Industry */}
      <UpcomingTrainingsSection
        pageSlug={industrySlug}
        title={`Upcoming ${industry.name} Training Sessions`}
      />

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-12 text-center text-white"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Transform Your {industry.name} Skills?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of professionals who have advanced their careers with our industry-specific training programs
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg"
                onClick={() => navigate('/training-calendar')}
              >
                View Training Calendar
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => navigate('/contact')}
              >
                Request Custom Training
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};
